import clsx from 'clsx';
import React, { useRef, useState } from 'react';
import { formatPhone } from '../../shared/phone';
import { formatShortDateTime } from '../../shared/time';
import {
  Avatar,
  Button,
  Callout,
  IconChevronDown,
  IconChevronRight,
  IconFlag,
  IconMail,
  IconNote,
  IconPhone,
  Kbd,
  Textarea,
  TranscriptView,
} from '../../ui/v2';
import type { ActivityItem } from './model';

/**
 * One chronological stream: Arthur's call (transcript inline), the team
 * email, state changes and the follow-up notes, with the note composer
 * directly after. Hairline spine, a 20px marker per item, no cards.
 */

const kindIcon: Record<ActivityItem['kind'], React.ReactNode> = {
  call: <IconPhone size={12} />,
  email: <IconMail size={12} />,
  note: <IconNote size={12} />,
  system: <IconFlag size={12} />,
};

const Marker: React.FC<{ item: ActivityItem }> = ({ item }) => {
  if (item.actor === 'arthur') return <Avatar arthur size="xs" />;
  if (item.actor !== 'system' && item.kind === 'note')
    return <Avatar name={item.actor.name} size="xs" />;
  return (
    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-v2-surface text-v2-fg-3 ring-1 ring-inset ring-v2-line">
      {kindIcon[item.kind]}
    </span>
  );
};

const Item: React.FC<{
  item: ActivityItem;
  guestName: string | null;
  last: boolean;
  defaultOpen?: boolean;
}> = ({ item, guestName, last, defaultOpen }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const failed = item.kind === 'email' && item.actor === 'system';
  const minor = item.kind === 'system';
  const expandable =
    item.kind === 'call' || (item.kind === 'email' && !failed && !!item.body);

  return (
    <li className="relative flex gap-3">
      <div className="flex w-5 shrink-0 flex-col items-center">
        <Marker item={item} />
        {!last && <span aria-hidden className="mt-1 w-px flex-1 bg-v2-line" />}
      </div>
      <div
        className={clsx('min-w-0 flex-1', last ? '' : minor ? 'pb-3' : 'pb-5')}
      >
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 leading-5">
          <span
            className={clsx(
              'text-v2-13',
              minor ? 'text-v2-fg-2' : 'font-medium text-v2-fg',
              failed && 'text-v2-danger',
            )}
          >
            {item.title}
          </span>
          <time dateTime={item.at} className="v2-num text-v2-12 text-v2-fg-3">
            {formatShortDateTime(item.at)}
          </time>
          {item.kind === 'call' && item.meta?.from && (
            <span className="v2-num text-v2-12 text-v2-fg-3">
              · from {formatPhone(item.meta.from)}
            </span>
          )}
        </div>

        {item.kind === 'note' && item.body && (
          <p className="mt-1 max-w-[68ch] whitespace-pre-wrap text-v2-13 leading-5 text-v2-fg-2">
            {item.body}
          </p>
        )}
        {failed && item.body && (
          <p className="mt-0.5 max-w-[68ch] text-v2-12 leading-[18px] text-v2-fg-3">
            {item.body}
          </p>
        )}

        {expandable && (
          <div className="mt-1.5">
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
              className="-ml-1 inline-flex h-6 items-center gap-1 rounded-v2 px-1 text-v2-12 font-medium text-v2-fg-2 hover:bg-v2-sunken hover:text-v2-fg"
            >
              {open ? (
                <IconChevronDown size={14} />
              ) : (
                <IconChevronRight size={14} />
              )}
              {item.kind === 'call'
                ? open
                  ? 'Hide transcript'
                  : 'Show transcript'
                : open
                  ? 'Hide email'
                  : 'Show email'}
            </button>
            {open && (
              <div className="v2-fade-in mt-2 rounded-v2 border border-v2-line bg-v2-sunken/40 p-3">
                {item.kind === 'call' ? (
                  <TranscriptView
                    transcript={item.transcript}
                    guestLabel={guestName?.split(' ')[0] || 'Caller'}
                  />
                ) : (
                  <>
                    {item.meta?.subject && (
                      <p className="mb-2 text-v2-12 font-medium text-v2-fg-2">
                        {item.meta.subject}
                      </p>
                    )}
                    <pre className="max-w-[72ch] whitespace-pre-wrap font-sans text-v2-12 leading-[18px] text-v2-fg-2">
                      {item.body}
                    </pre>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  );
};

export const InquiryActivity: React.FC<{
  items: ActivityItem[];
  guestName: string | null;
  onAddNote: (text: string) => Promise<unknown>;
  authorName?: string;
}> = ({ items, guestName, onAddNote, authorName }) => {
  const [text, setText] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const addingRef = useRef(false);

  const submit = async () => {
    const t = text.trim();
    if (!t || addingRef.current) return;
    addingRef.current = true;
    setAdding(true);
    setError(null);
    try {
      await onAddNote(t);
      setText('');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      addingRef.current = false;
      setAdding(false);
    }
  };

  const firstCall = items.find((i) => i.kind === 'call')?.id;
  return (
    <div>
      <ol className="flex flex-col">
        {items.map((it, i) => (
          <Item
            key={it.id}
            item={it}
            guestName={guestName}
            last={i === items.length - 1}
            defaultOpen={it.id === firstCall && items.length <= 2}
          />
        ))}
      </ol>
      <div className="mt-5 flex gap-3">
        <div className="flex w-5 shrink-0 justify-center pt-1.5">
          {authorName ? (
            <Avatar name={authorName} size="xs" />
          ) : (
            <span className="h-5 w-5 rounded-full bg-v2-sunken ring-1 ring-inset ring-v2-line" />
          )}
        </div>
        <div className="flex-1">
          {error && (
            <Callout tone="danger" className="mb-2">
              {error}
            </Callout>
          )}
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                void submit();
              }
            }}
            rows={2}
            placeholder="Add an internal note — what you told the guest, what they asked for, what’s next…"
            aria-label="Add an internal note"
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <span className="text-v2-11 text-v2-fg-4">
              <Kbd>⌘</Kbd> <Kbd>↵</Kbd> to save · Saved to this inquiry’s
              follow-up notes
            </span>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void submit()}
              disabled={!text.trim()}
              loading={adding}
            >
              Add note
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
